import {
  BaseSensorEntry,
  StandaloneChart,
  TimeSeriesDerivedValueEntryJSON,
} from 'types'

export interface ScatterplotInitializationParams {
  chart: StandaloneChart | null | undefined
  chartError?: Error | null
  isLoadingChart: boolean
  sensorEntries: BaseSensorEntry[]
  sensorEntriesError?: Error | null
  sensorEntriesLoaded: boolean
  isLoadingSensorEntries: boolean
  derivedValueEntries: TimeSeriesDerivedValueEntryJSON[]
  derivedValueEntriesLoaded: boolean
  isLoadingDerivedValueEntries: boolean
  initialSensorIds?: string[]
  overrideSensorIds?: string[]
  overrideTimeRange?: {
    from: string
    to: string
    interval?: number
    intervalUnit?: string
  }
  selectedSensors: BaseSensorEntry[]
  derivedValues: TimeSeriesDerivedValueEntryJSON[]
  appliedInitialSensorIds: ReadonlySet<string>
  appliedOverrideSensorIdsKey: string | null
  appliedOverrideTimeRangeKey: string | null
  disableSaving: boolean
  transient: boolean
  /**
   * True once initialization has settled for the current chart id. The
   * "chart pens must be selected" checks below exist to hold the provider in
   * its initializing state until the one-shot config apply lands — but the
   * exact same shape (config names a pen that is not selected) recurs when
   * the user REMOVES a config-named pen afterwards. Re-tripping then wedges
   * everything gated on `!isInitializing`: the local-state writer never
   * persists the removal (a refresh resurrects the pen) and the chart spinner
   * re-appears. Once settled, the config-selection comparison is skipped;
   * loading/override checks still apply.
   */
  hasSettledForChart?: boolean
}

export const buildSensorSelectionKey = (
  sensorIds: string[] | undefined
): string | null => {
  if (!sensorIds || sensorIds.length === 0) {
    return null
  }

  return JSON.stringify([...sensorIds].sort())
}

export const buildTimeRangeKey = (
  timeRange:
    | { from: string; to: string; interval?: number; intervalUnit?: string }
    | undefined
): string | null => {
  if (!timeRange) {
    return null
  }

  return `${timeRange.from}::${timeRange.to}::${timeRange.interval ?? ''}${timeRange.intervalUnit ?? ''}`
}

export const getScatterplotIsInitializing = ({
  chart,
  chartError,
  isLoadingChart,
  sensorEntries,
  sensorEntriesError,
  sensorEntriesLoaded,
  isLoadingSensorEntries,
  derivedValueEntries,
  derivedValueEntriesLoaded,
  isLoadingDerivedValueEntries,
  initialSensorIds,
  overrideSensorIds,
  overrideTimeRange,
  selectedSensors,
  derivedValues,
  appliedInitialSensorIds,
  appliedOverrideSensorIdsKey,
  appliedOverrideTimeRangeKey,
  disableSaving,
  transient,
  hasSettledForChart = false,
}: ScatterplotInitializationParams): boolean => {
  const requiresChartResolution = !disableSaving && !transient
  const chartSensorSourceIds =
    chart?.sourceConfigs
      .filter((sourceConfig) => sourceConfig.type === 'sensor')
      .map((sourceConfig) => sourceConfig.sourceId) ?? []
  const chartDerivedValueSourceIds =
    chart?.sourceConfigs
      .filter((sourceConfig) => sourceConfig.type === 'derivedValue')
      .map((sourceConfig) => sourceConfig.sourceId) ?? []

  if (requiresChartResolution && (isLoadingChart || (!chart && !chartError))) {
    return true
  }

  const needsSensorEntries =
    chartSensorSourceIds.length > 0 ||
    (initialSensorIds?.length ?? 0) > 0 ||
    (overrideSensorIds?.length ?? 0) > 0

  if (
    needsSensorEntries &&
    (isLoadingSensorEntries ||
      !sensorEntriesLoaded ||
      (sensorEntriesError !== null && sensorEntries.length === 0))
  ) {
    return true
  }

  const needsDerivedValueEntries = chartDerivedValueSourceIds.length > 0

  if (
    needsDerivedValueEntries &&
    (isLoadingDerivedValueEntries || !derivedValueEntriesLoaded)
  ) {
    return true
  }

  const availableSensorIds = new Set(sensorEntries.map((sensor) => sensor.id))
  const selectedSensorIds = new Set(
    selectedSensors.map((selectedSensor) => selectedSensor.id)
  )
  const availableDerivedValueIds = new Set(
    derivedValueEntries.map((derivedValue) => derivedValue.id)
  )
  const selectedDerivedValueIds = new Set(
    derivedValues.map((derivedValue) => derivedValue.id)
  )

  // Only compared until the first settle for this chart id — afterwards a
  // config-named pen missing from the selection means the user removed it,
  // not that initialization regressed (see `hasSettledForChart`).
  if (!hasSettledForChart) {
    const matchedChartSensorIds = chartSensorSourceIds.filter((sensorId) =>
      availableSensorIds.has(sensorId)
    )
    if (
      matchedChartSensorIds.some((sensorId) => !selectedSensorIds.has(sensorId))
    ) {
      return true
    }

    const matchedChartDerivedValueIds = chartDerivedValueSourceIds.filter(
      (derivedValueId) => availableDerivedValueIds.has(derivedValueId)
    )
    if (
      matchedChartDerivedValueIds.some(
        (derivedValueId) => !selectedDerivedValueIds.has(derivedValueId)
      )
    ) {
      return true
    }
  }

  const matchedInitialSensorIds = (initialSensorIds ?? []).filter((sensorId) =>
    availableSensorIds.has(sensorId)
  )
  if (
    matchedInitialSensorIds.length > 0 &&
    matchedInitialSensorIds.some(
      (sensorId) => !appliedInitialSensorIds.has(sensorId)
    )
  ) {
    return true
  }

  const overrideSensorSelectionKey = buildSensorSelectionKey(overrideSensorIds)
  const matchedOverrideSensorIds = (overrideSensorIds ?? []).filter(
    (sensorId) => availableSensorIds.has(sensorId)
  )
  if (
    matchedOverrideSensorIds.length > 0 &&
    appliedOverrideSensorIdsKey !== overrideSensorSelectionKey
  ) {
    return true
  }

  const overrideTimeRangeKey = buildTimeRangeKey(overrideTimeRange)
  if (
    overrideTimeRangeKey &&
    appliedOverrideTimeRangeKey !== overrideTimeRangeKey
  ) {
    return true
  }

  return false
}
